import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import Spinner from "@/app/components/Spinner";
import { Button } from "@/app/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/app/components/ui/sheet";
import { sanitizeFileName } from "@/app/handlers/documentStorage";
import { docSubTypes, dspDocTypes } from "@/app/handlers/enums";
import { getErrorMessage } from "@/app/handlers/errorHandling";
import { generateFileHash } from "@/app/handlers/utilityFunctions";
import { useGoogleSignIn } from "@/app/hooks/useGoogleSignIn";
import { API } from "@/app/services/apiService";
import type { GoogleSession } from "@/app/services/auth/googleSignIn";
import FileUploadService from "@/app/services/fileUploadService";
import FileUpload from "@/app/Views/Profile/components/FileUpload";

import GoogleSignInButton from "./GoogleSignInButton";

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];
const SHEET_TRAVEL = 400;
const PDF_ONLY = [{ name: "PDF", extension: ".pdf", mimeType: "application/pdf" }];

async function uploadResume(file: File) {
	const description = (file.name ?? "").slice(0, 140);

	const { data } = await API.post("smart-profiles", {
		dsp_title: "My Resume",
		doc_description: description,
		dsp_description: description,
		dsp_doc_type: dspDocTypes.RESUME,
		doc_sub_type: docSubTypes.RESUME.name,
		file_name: sanitizeFileName(file.name),
		file_hash: await generateFileHash(file),
	});

	const { uploadUrl, content_type: contentType } = data?.upload_info ?? data ?? {};

	await new Promise<void>((resolve, reject) => {
		FileUploadService.uploadResumeFileToBucketWithFetchAPI({ uploadUrl, file, contentType }, () => resolve(), reject);
	});

	return uploadUrl.split("?")[0] as string;
}

interface SignInSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description: string;
	resumeTitle: string;
	resumeDescription: string;
	onSignedIn: (session: GoogleSession, context: { hasAccount: boolean }) => Promise<string | null>;
	onResumeReady: (resumeUrl: string) => Promise<void>;
	busyLabel: string;
}

function SignInSheet({
	open,
	onOpenChange,
	title,
	description,
	resumeTitle,
	resumeDescription,
	onSignedIn,
	onResumeReady,
	busyLabel,
}: SignInSheetProps) {
	const reduceMotion = useReducedMotion();
	const [phase, setPhase] = useState<"signin" | "working" | "resume">("signin");
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const session = useRef<GoogleSession | null>(null);
	const hasAccount = useRef(false);

	useEffect(() => {
		if (open) return;
		setPhase("signin");
		setFile(null);
		setError("");
		setBusy(false);
	}, [open]);

	const signedIn = async (googleSession: GoogleSession, accountExists: boolean) => {
		session.current = googleSession;
		hasAccount.current = accountExists;
		setPhase("working");

		const resumeUrl = await onSignedIn(googleSession, { hasAccount: accountExists });
		if (resumeUrl) void onResumeReady(resumeUrl);
		else setPhase("resume");
	};

	const { handleGoogleSignIn, handleGoogleError } = useGoogleSignIn({
		onExistingAccount: (googleSession) => void signedIn(googleSession, true),
		onNewAccount: (googleSession) => void signedIn(googleSession, false),
		onAccountCheckFailed: (googleSession) => void signedIn(googleSession, false),
	});

	const submitResume = async () => {
		if (!session.current || !file) return;
		setBusy(true);
		setError("");

		try {
			if (!hasAccount.current) {
				const { email, firstName, lastName } = session.current.profile;
				await API.post("users", { emailAddress: email, firstName, lastName, jobPreferences: {} });
			}
			const resumeUrl = await uploadResume(file);
			setPhase("working");
			await onResumeReady(resumeUrl);
		} catch (uploadFailure) {
			setError(getErrorMessage(uploadFailure, "We couldn't upload your resume. Please try again."));
			setBusy(false);
		}
	};

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<AnimatePresence>
				{open && (
					<SheetContent
						forceMount
						asChild
						side="bottom"
						className="pb-8 outline-none transition-none"
						overlayClassName="bg-black/25 backdrop-blur-none supports-backdrop-filter:backdrop-blur-none transition-opacity duration-200 data-closed:opacity-0"
					>
						<motion.div
							initial={{ y: SHEET_TRAVEL }}
							animate={{ y: 0 }}
							exit={{ y: SHEET_TRAVEL }}
							transition={{ duration: reduceMotion ? 0 : 0.28, ease: EASE }}
						>
							<SheetHeader className="gap-2 pt-6 text-center">
								<SheetTitle className="text-lg font-semibold">{phase === "resume" ? resumeTitle : title}</SheetTitle>
								<SheetDescription>{phase === "resume" ? resumeDescription : description}</SheetDescription>
							</SheetHeader>

							<div className="mx-auto w-full max-w-sm px-4 pb-2">
								{phase === "signin" && (
									<GoogleSignInButton onSuccess={handleGoogleSignIn} onError={handleGoogleError} />
								)}

								{phase === "working" && (
									<div className="flex items-center justify-center gap-2 py-2 text-sm text-gray-600">
										<div className="size-4">
											<Spinner />
										</div>
										{busyLabel}
									</div>
								)}

								{phase === "resume" && (
									<div className="flex flex-col gap-3">
										<FileUpload
											processDataOfUploadedFile={(uploaded) => setFile(uploaded as File)}
											removeFile={() => setFile(null)}
											fileName={file?.name || ""}
											fileError={error}
											setFileError={(message) => setError(message || "")}
											multiple={false}
											supportedFiles={PDF_ONLY}
											disableFileInput={busy}
										/>
										<Button className="w-full" type="button" disabled={!file || busy} onClick={submitResume}>
											{busy ? <Spinner /> : "Check my fit"}
										</Button>
									</div>
								)}
							</div>
						</motion.div>
					</SheetContent>
				)}
			</AnimatePresence>
		</Sheet>
	);
}

export default SignInSheet;
